// "To loading bay" on a pallet's row in the Coldroom cell drawer (F05 UF-02).
// The pallet's next step for an export pallet: the shared lane picker, then
// transitionPallet() — the one writer the Pallets card and the pallet page use.
// Works whatever the pallet's run is doing, so a completed run no longer needs
// an admin to reopen it.
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import LanePicker from '@/components/LanePicker'
import MutationOverlays from '@/components/MutationOverlays'
import { transitionPallet } from '@/lib/palletTransition'
import { useCurrentUser } from '@/lib/useCurrentUser'
import { ValidationError } from '@/lib/errorReporter'

export interface LoadingBayPallet {
  pallet_id:     string
  version:       number
  tenant_id:     string | null
  channel:       string | null
  target_market: string | null
  pallet_type:   string | null
  spec:          string | null
  grade:         string | null
  size_code:     string | null
  pack_type:     string | null
  destination:   string | null
  packed_at:     string
  variety:       string | null
}

/** Export by the same rule as the dispatch_mission gate: the channel column
 *  decides; a legacy row with no channel counts as export when it has a target
 *  market. A LOCAL pallet leaves straight from cold on a local truck. */
export function offersLoadingBay(p: Pick<LoadingBayPallet, 'channel' | 'target_market'>): boolean {
  return p.channel === 'EXPORT' || (p.channel == null && p.target_market != null)
}

export default function ToLoadingBay({ pallet, tenantId, onClose, onDone }: {
  pallet:   LoadingBayPallet
  tenantId: string | null
  onClose:  () => void
  /** Called once the move is saved, with the lane it went to. */
  onDone:   (lane: string) => void
}) {
  const qc = useQueryClient()
  const { data: me } = useCurrentUser()
  // Row security refuses another business's pallet.
  const mine = !!tenantId && pallet.tenant_id === tenantId
  const [lane, setLane] = useState<string | null>(null)
  const [open, setOpen] = useState(true)

  const place = useMutation({
    mutationKey: ['coldroom-to-loading-bay'],
    mutationFn: async (laneCode: string) => {
      if (!me) throw new ValidationError('Not authenticated')
      setLane(laneCode)
      await transitionPallet({ pallet, toStatus: 'IN_LOADING_AREA', extra: { loading_bay_lane: laneCode }, meId: me.id })
    },
    onError: () => { qc.invalidateQueries({ queryKey: ['coldroom-grid'] }) },
    onSuccess: (_d, laneCode) => {
      setOpen(false)
      for (const k of ['coldroom-grid', 'coldroom-occupied', 'coldroom-counts', 'pallets', 'lane-picker-data']) {
        qc.invalidateQueries({ queryKey: [k] })
      }
      onDone(laneCode)
    },
  })

  return (
    <>
      {mine && open && (
        <LanePicker
          busy={place.isPending}
          pallet={{
            pallet_id: pallet.pallet_id, pallet_type: pallet.pallet_type, spec: pallet.spec,
            variety: pallet.variety, grade: pallet.grade, size_code: pallet.size_code,
            pack_type: pallet.pack_type, destination: pallet.destination, packed_at: pallet.packed_at,
          }}
          onCancel={onClose}
          onPick={code => place.mutate(code)}
        />
      )}
      <MutationOverlays of={[[place, lane ? `${pallet.pallet_id} → loading bay, lane ${lane}` : 'Moved to loading bay']]} />
    </>
  )
}
